"use client"
import { useEffect, useState } from "react";
import { indicatorIcons } from "@/data/mappings";
import { MessageStatus, variantStyles } from "./constants";
import { CloseIcon } from "@/components/icons";

interface ToastProps {
  status?: MessageStatus;
  message?: string;
  onClose?: () => void;
}

export default function Toast({ status, message, onClose }: ToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (message && status) {
      setIsVisible(true);

      // Auto close after 4s with fade-out
      const timer = setTimeout(() => {
        handleClose();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [message, status]);

  const handleClose = () => {
    setIsClosing(true);
    // Wait for fade-out animation to finish before removing toast
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
      onClose?.();
    }, 400); // match fade-out duration in CSS
  };

  if (!message || !status || !isVisible) return null;

  const Icon = indicatorIcons[status];

  return (
    <div
      className={`fixed bottom-6 right-6 flex items-center gap-3 p-4 border border-gray-200 justify-between text-[#1b1d2b]
        rounded-2xl shadow-lg transition-all duration-300 z-50 min-w-[330px]
        bg-white ${
          isClosing ? "animate-fade-out-down" : "animate-fade-in-up"
        }`}
    >
      <div className="flex items-center gap-3">
        <Icon color={variantStyles[status]} width={25} height={25} />
        <p className="font-normal text-[15px] max-w-[225px]">{message}</p>
      </div>

      <button
        aria-label="close-toast"
        onClick={handleClose}
        className="p-1 rounded-full transition-all duration-200 hover:bg-gray-100"
      >
        <CloseIcon width={20} height={20} color="gray" className="cursor-pointer" />
      </button>
    </div>
  );
}
